import { getService } from '@Client/runner.hooks/useService';
import DeleteModal from '@Shared.Angular/flowingly.components/DeleteModal/DeleteModal';
import KendoReactEditor, {
  EditorChangeEvent
} from '@Shared.Angular/kendo.react/KendoReactEditor';
import IProcedureActionDetail from '@Shared.Angular/@types/core/contracts/queryModel/procedure/procedureActionDetail';
import React, { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { EmptyGuid, Guid } from '@Shared.Angular/@types/guid';
import ProcedureActionOverlayEditor from './ProcedureActionOverlayEditor';
import ProcedureActionImage from '@Shared.Angular/flowingly.components/Procedures/ProcedureActionImage';
import { FileUpload } from '@Shared.Angular/flowingly.components/FileUpload/FileUpload';
import OverlayType from '@Shared.Angular/@types/core/contracts/queryModel/procedure/overlayType';
import IProcedureActionOverlayDetail from '@Shared.Angular/@types/core/contracts/queryModel/procedure/procedureActionOverlayDetail';
import IconButton from '@Shared.Angular/flowingly.components/buttons/IconButton';
import { TextButton } from '@Shared.Angular/flowingly.components/buttons/TextButton';
import { FileFolder } from '@Shared.Angular/flowingly.services/file.service';

type Props = {
  headerNumber: number;
  action: IProcedureActionDetail;
  editorTools: unknown[];
  readOnly?: boolean;
  isEditing?: boolean;
  persistChanges: (action: IProcedureActionDetail) => void;
  onImageClick: () => void;
  addActionAfterIndex?: (index: number) => void;
};

const ProcedureActionEditor = ({
  headerNumber,
  action,
  editorTools,
  readOnly,
  isEditing,
  persistChanges,
  onImageClick,
  addActionAfterIndex
}: Props) => {
  const [editedAction, setEditedAction] = useState<IProcedureActionDetail>({
    ...action
  });
  useEffect(() => {
    setEditedAction({
      ...action
    });
  }, [action]);

  const [isEditMode, setIsEditMode] = useState(isEditing || false);
  const [isRedactMode, setIsRedactMode] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isOptionsMenuOpen, setIsOptionsMenuOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [originalImageDataUrl, setOriginalImageDataUrl] = useState('');
  const [modifiedImageDataUrl, setModifiedImageDataUrl] = useState('');

  const updateTitle = (e: ChangeEvent<HTMLInputElement>) => {
    setEditedAction({ ...editedAction, title: e.target.value });
  };
  const updateDescription = (e: EditorChangeEvent) => {
    setEditedAction({ ...editedAction, description: e.html });
  };
  const storeUploadedFile = (fileId: Guid) => {
    const overlays = [
      {
        id: EmptyGuid,
        procedureActionId: editedAction.id,
        x: 20,
        y: 20,
        width: 100,
        height: 100,
        type: OverlayType.Highlight
      }
    ];

    setEditedAction({
      ...editedAction,
      originalFileId: fileId,
      modifiedFileId: null,
      overlays: overlays
    });
    setModifiedImageDataUrl('');
    fileService.getFileById(fileId).then((file) => {
      if (file && file.data) {
        const objectUrl = URL.createObjectURL(file.data);
        setOriginalImageDataUrl(objectUrl);
      }
    });
  };

  const procedureApiService = getService('procedureApiService');
  const fileService = getService('fileService');
  const notificationService = getService('notificationService');

  useEffect(() => {
    setTimeout(
      () => {
        const $q = getService('$q');
        const filePromises = [
          editedAction.originalFileId
            ? fileService.getFileById(editedAction.originalFileId)
            : $q.resolve(null),
          editedAction.modifiedFileId
            ? fileService.getFileById(editedAction.modifiedFileId)
            : $q.resolve(null)
        ];
        $q.all(filePromises).then((responses) => {
          const originalResponse = responses[0];
          if (originalResponse && originalResponse.data) {
            setOriginalImageDataUrl(URL.createObjectURL(originalResponse.data));
          }
          const modifiedResponse = responses[1];
          if (modifiedResponse && modifiedResponse.data) {
            setModifiedImageDataUrl(URL.createObjectURL(modifiedResponse.data));
          }
        });
      },
      // Stagger initial loading of files so we don't hammer the API
      originalImageDataUrl || modifiedImageDataUrl
        ? 0
        : editedAction.order * 400
    );
  }, [editedAction.originalFileId, editedAction.modifiedFileId]);

  useEffect(() => {
    return () => {
      if (originalImageDataUrl) URL.revokeObjectURL(originalImageDataUrl);
    };
  }, [originalImageDataUrl]);

  useEffect(() => {
    return () => {
      if (modifiedImageDataUrl) URL.revokeObjectURL(modifiedImageDataUrl);
    };
  }, [modifiedImageDataUrl]);

  const saveChanges = () => {
    setIsSaving(true);
    const redactionDone = isRedactMode
      ? redactImage(originalImageDataUrl, editedAction)
      : Promise.resolve(null);
    redactionDone
      .then((newFileId: Guid | null) => {
        const updatedAction = { ...editedAction };
        if (newFileId) {
          updatedAction.modifiedFileId = newFileId;
        }
        return procedureApiService
          .updateProcedureAction(updatedAction)
          .then(() => {
            persistChanges(updatedAction);
            setIsEditMode(false);
            setIsRedactMode(false);
          });
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  const redactImage = useCallback(
    (baseImageDataUrl: string, currentEditedAction: IProcedureActionDetail) => {
      return new Promise<Guid>((resolve, reject) => {
        if (!baseImageDataUrl) {
          reject();
          return;
        }
        const image = new Image();
        image.src = baseImageDataUrl;
        image.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = image.naturalWidth;
          canvas.height = image.naturalHeight;
          const mainImageContext = canvas.getContext('2d');
          mainImageContext.drawImage(image, 0, 0);
          currentEditedAction.overlays
            .filter((o) => o.type === OverlayType.Redaction)
            .forEach((overlay) => {
              const blurredSection = createBlurredImageSection(
                image,
                overlay.x,
                overlay.y,
                overlay.width,
                overlay.height
              );

              mainImageContext.fillStyle = 'white';
              mainImageContext.fillRect(
                overlay.x,
                overlay.y,
                overlay.width,
                overlay.height
              );
              mainImageContext.drawImage(blurredSection, overlay.x, overlay.y);
            });
          canvas.toBlob((blob) => {
            if (!blob) {
              reject();
              return;
            }
            const file = new File([blob], 'redacted_image.png', {
              type: blob.type
            });
            fileService
              .uploadFileToFolder(file, FileFolder.Procedures)
              .then((response) => {
                const newFileId = response.data;
                resolve(newFileId);
              })
              .catch(() => {
                reject();
              });
          });
        };
      });
    },
    []
  );

  const createBlurredImageSection = useCallback(
    (
      source: HTMLImageElement,
      x: number,
      y: number,
      width: number,
      height: number
    ) => {
      const tempCanvas = document.createElement('canvas');
      const tempCtx = tempCanvas.getContext('2d');

      tempCanvas.width = width;
      tempCanvas.height = height;
      tempCtx.filter = 'saturate(0%)';
      tempCtx.drawImage(
        source,
        x,
        y,
        width,
        height,
        0,
        0,
        tempCanvas.width,
        tempCanvas.height
      );

      // Safari doesn't support filters, so we do a manual down/upsample to simulate blur
      const downsampleFactor = 5;
      tempCtx.drawImage(
        tempCanvas,
        0,
        0,
        tempCanvas.width / downsampleFactor,
        tempCanvas.height / downsampleFactor
      );

      const finalCanvas = document.createElement('canvas');
      finalCanvas.width = width;
      finalCanvas.height = height;
      const finalCtx = finalCanvas.getContext('2d');

      finalCtx.filter = 'blur(10px)';
      finalCtx.drawImage(
        tempCanvas,
        0,
        0,
        tempCanvas.width / downsampleFactor,
        tempCanvas.height / downsampleFactor,
        0,
        0,
        width,
        height
      );

      return finalCanvas;
    },
    []
  );

  const persistUpdatedOverlay = (
    id: Guid,
    x: number,
    y: number,
    width: number,
    height: number
  ) => {
    const overlay = editedAction.overlays.find((o) => o.id === id);
    if (overlay) {
      overlay.x = x;
      overlay.y = y;
      overlay.width = width;
      overlay.height = height;
    }
  };

  const deleteAction = () => {
    procedureApiService
      .deleteProcedureAction(editedAction.procedureId, editedAction.id)
      .then(() => {
        notificationService.showSuccessToast('Action has been deleted.');
        setIsDeleteModalOpen(false);
        editedAction.active = false;
        persistChanges(editedAction);
        setIsEditMode(false);
      })
      .catch(() => {
        notificationService.showErrorToast(
          'An error occurred while deleting the Action.'
        );
      });
  };

  const addOverlay = (type: OverlayType) => {
    const newOverlay: IProcedureActionOverlayDetail = {
      id: getService('guidService').new(),
      procedureActionId: editedAction.id,
      x: 20,
      y: 20,
      width: 100,
      height: 100,
      type: type
    };
    setEditedAction({
      ...editedAction,
      overlays: [...(editedAction.overlays || []), newOverlay]
    });
  };

  const removeOverlay = (id: Guid) => {
    setEditedAction({
      ...editedAction,
      overlays: editedAction.overlays.filter((o) => o.id !== id)
    });
  };

  const discardChanges = () => {
    setEditedAction({
      ...action
    });
    setIsEditMode(false);
    setIsRedactMode(false);
  };

  const defaultImageDataUrl = modifiedImageDataUrl || originalImageDataUrl;

  if (!action) return null;

  return (
    <>
      {isEditMode && (
        <div
          className="procedure-action editor"
          id={'action_' + editedAction.id}
        >
          <div className="title-container">
            <div className="title">
              <label htmlFor="event-title">Title *</label>
              <input
                type="text"
                className="procedure-title"
                value={editedAction.title}
                onChange={updateTitle}
              ></input>
              <div className="title-warning error-msg">
                {editedAction.title ? '' : 'Title is required'}
              </div>
            </div>
          </div>
          <div>
            <label>Description</label>
            <KendoReactEditor
              tools={editorTools}
              defaultContent={editedAction.description || ''}
              onChange={updateDescription}
              resizable={true}
              contentStyle={{ height: '100%' }}
              style={{ resize: 'vertical' }}
            />
          </div>
          <div className="editor-toolbar highlight-editor">
            {defaultImageDataUrl && (
              <IconButton
                iconClass="fa-layer-plus"
                title="Add Highlight"
                onClick={() => addOverlay(OverlayType.Highlight)}
              />
            )}
            <TextButton
              text="Save"
              type="secondary"
              disabled={!editedAction.title || isSaving}
              onClick={saveChanges}
            />
            <TextButton
              text="Cancel"
              type="text-only"
              onClick={discardChanges}
            />
          </div>
          {defaultImageDataUrl && (
            <ProcedureActionOverlayEditor
              imageDataUrl={defaultImageDataUrl}
              overlays={editedAction.overlays.filter(
                (o) => o.type === OverlayType.Highlight
              )}
              onOverlayUpdated={persistUpdatedOverlay}
              onOverlayDeleted={removeOverlay}
            ></ProcedureActionOverlayEditor>
          )}
          <FileUpload
            title={
              editedAction.originalFileId ? 'Replace image' : 'Upload image'
            }
            onUpload={storeUploadedFile}
            acceptTypes={['image/*']}
            uploadFolder={FileFolder.Procedures}
          />
        </div>
      )}
      {!isEditMode && (
        <div
          className="procedure-action editor readonly"
          id={'action_' + editedAction.id}
        >
          <div className="header">
            <div className="procedure-field">
              <div className="field-index">
                {headerNumber < 10 ? '0' : ''}
                {headerNumber}
              </div>
              <div className="field-title">{action.title}</div>
            </div>
            {!readOnly && (
              <div className="editor-toolbar">
                {!isRedactMode && (
                  <>
                    {originalImageDataUrl && (
                      <IconButton
                        iconClass="fa-text-slash"
                        title="Redact"
                        onClick={() => setIsRedactMode(true)}
                      />
                    )}
                    <IconButton
                      iconClass="fa-pencil"
                      title="Edit"
                      onClick={() => setIsEditMode(true)}
                    />
                    <div className="options-menu-anchor">
                      <IconButton
                        iconClass="fa-ellipsis-stroke"
                        title="More options"
                        data-testid="procedure-action-options-button"
                        onClick={() => {
                          setIsOptionsMenuOpen(!isOptionsMenuOpen);
                        }}
                      />
                      {isOptionsMenuOpen && (
                        <div
                          className="options-menu"
                          onMouseLeave={() => setIsOptionsMenuOpen(false)}
                        >
                          <div
                            className="option-item"
                            data-testid="procedure-action-add-step-above"
                            onClick={() => {
                              setIsOptionsMenuOpen(false);
                              addActionAfterIndex(headerNumber - 2);
                            }}
                          >
                            <i className="fa-light fa-page-caret-up"></i>
                            Add step above
                          </div>
                          <div
                            className="option-item"
                            data-testid="procedure-action-add-step-below"
                            onClick={() => {
                              setIsOptionsMenuOpen(false);
                              addActionAfterIndex(headerNumber - 1);
                            }}
                          >
                            <i className="fa-light fa-page-caret-down"></i>
                            Add step below
                          </div>
                          <div
                            className="option-item"
                            data-testid="procedure-action-delete-step"
                            onClick={() => {
                              setIsDeleteModalOpen(true);
                              setIsOptionsMenuOpen(false);
                            }}
                          >
                            <i className="fa-light fa-trash-can"></i>
                            Delete step
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                )}
                {isRedactMode && (
                  <>
                    <TextButton
                      type="text-only"
                      text="Cancel"
                      onClick={discardChanges}
                    />
                    <TextButton
                      type="secondary"
                      text="Apply Redactions"
                      disabled={isSaving}
                      onClick={saveChanges}
                    />
                    <IconButton
                      iconClass="fa-layer-plus"
                      title="Add Redaction"
                      pulse={
                        !editedAction.overlays.some(
                          (o) => o.type === OverlayType.Redaction
                        )
                      }
                      onClick={() => addOverlay(OverlayType.Redaction)}
                    />
                  </>
                )}
              </div>
            )}
          </div>
          {action.description && (
            <div
              className="kendo-rich-text"
              dangerouslySetInnerHTML={{
                __html: action.description
              }}
            ></div>
          )}
          {!isRedactMode && defaultImageDataUrl && (
            <ProcedureActionImage
              imageDataUrl={defaultImageDataUrl}
              overlays={action.overlays.filter(
                (o) => o.type === OverlayType.Highlight
              )}
              onImageClick={onImageClick}
            />
          )}
          {isRedactMode && (
            <ProcedureActionOverlayEditor
              imageDataUrl={originalImageDataUrl}
              overlays={editedAction.overlays.filter(
                (o) => o.type === OverlayType.Redaction
              )}
              onOverlayUpdated={persistUpdatedOverlay}
              onOverlayDeleted={removeOverlay}
            ></ProcedureActionOverlayEditor>
          )}
          {isDeleteModalOpen && (
            <DeleteModal
              title="Delete SOP Action"
              subject={editedAction.title}
              closeModal={() => setIsDeleteModalOpen(false)}
              deleteAction={deleteAction}
              confirmationWord="delete"
            ></DeleteModal>
          )}
        </div>
      )}
    </>
  );
};

export default ProcedureActionEditor;

export interface IImageDimensions {
  width: number;
  height: number;
  naturalWidth: number;
  naturalHeight: number;
}
